import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import FilterListIcon from "@material-ui/icons/FilterList";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import VideocamIcon from "@material-ui/icons/Videocam";
import LinkIcon from "@material-ui/icons/Link";
import DescriptionIcon from "@material-ui/icons/Description";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TicketsSearchResults from "../TicketsSearchResults";
import TabPanel from "../TabPanel";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";

const useStyles = makeStyles(theme => ({
	// Container principal - garante 100% altura e largura sem espaçamentos
	root: {
		height: "100%",
		width: "100%",
		padding: 0,
		margin: 0,
		display: "flex",
		flexDirection: "column",
		minHeight: 0,
		overflow: "hidden",
	},

	// Wrapper principal do tickets
	ticketsWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		width: "100%",
		flexDirection: "column",
		overflow: "hidden",
		minHeight: 0,
		borderRadius: 12,
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.neoUI.effects.glow.main,
		border: theme.neoUI.effects.contour.main,
		borderRight: "none", // 👈 Remove bordera direita para colar no Ticket/Mensagens
		// Controle total dos espaçamentos:
		marginTop: 0,
		marginRight: 0,    // 👈 Sem margem direita
		marginBottom: 0,
		marginLeft: 0,
		// Sem padding interno para colar nos componentes adjacentes
		padding: 0,
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
		[theme.breakpoints.down("sm")]: {
			borderRadius: 0,
			borderLeft: "none",
		},
	},

	ticketHeader: {
		flex: "0 0 auto",
		display: "flex",
		flexDirection: "column",
		minWidth: 0,
		backgroundColor: theme.palette.background.paper,
	},

	tabsHeader: {
		flex: "0 0 auto",
		backgroundColor: `${theme.palette.background.paper} !important`,
		borderBottom: `none`,
		height: 48,
		borderTopLeftRadius: 12,
		borderTopRightRadius: 0,
		[theme.breakpoints.down("sm")]: {
			borderTopLeftRadius: 0,
		},
		"& .MuiTabs-root": {
			height: 48,
			boxShadow: "none !important",
		},
		"& .MuiTabs-indicator": {
			height: 2,
		},
	},

	tabsInternal: {
		flex: "0 0 auto",
		backgroundColor: theme.palette.background.paper,
		borderBottom: `1px solid ${theme.palette.divider}`,
		height: 48,
		"& .MuiTabs-indicator": {
			height: 2,
		},
	},

	tab: {
		height: 48,
		minHeight: 0,
		minWidth: 100,
		padding: "4px 8px",
		textTransform: "none",
		fontWeight: 500,
		fontSize: "0.813rem",
		"& .MuiTab-wrapper": {
			flexDirection: "row",
			"& > *:first-child": {
				marginBottom: "0 !important",
				marginRight: 4,
			},
		},
		"&.Mui-selected": {
			fontWeight: 600,
		},
	},

	internalTab: {
		height: 48,
		minHeight: 48,
		minWidth: 100,
		padding: "4px 8px",
		textTransform: "none",
		fontWeight: 500,
		fontSize: "0.813rem",
		display: "flex",
		alignItems: "center",
		overflow: "visible",
		"& .MuiTab-wrapper": {
			flexDirection: "row",
			alignItems: "center",
			overflow: "visible",
			"& > *:first-child": {
				marginBottom: "0 !important",
				marginRight: 3,
			},
		},
		"&.Mui-selected": {
			fontWeight: 600,
		},
		[theme.breakpoints.down("xs")]: {
			minWidth: 0,
			padding: "4px 4px",
			fontSize: "0.75rem",
			"& .MuiTab-wrapper > *:first-child": {
				fontSize: 15,
				marginRight: 2,
			},
		},
		"@media (max-width: 359.95px)": {
			"& .MuiTab-wrapper > svg": { display: "none" },
		},
		"@media (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
			minWidth: 82,
			padding: "4px 2px",
			fontSize: "0.75rem",
			"& .MuiTab-wrapper": {
				"& > *:first-child": {
					marginRight: 1,
				},
			},
		},
	},

	ticketOptionsBox: {
		flex: "0 0 auto",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		backgroundColor: theme.palette.background.paper,
		padding: "8px 10px",
		borderBottom: "none",
		height: 48,
	},

	serachInputWrapper: {
		flex: 1,
		display: "flex",
		alignItems: "center",
		borderRadius: 16,
		backgroundColor: theme.palette.background.paper,
		padding: 6,
		height: 32,
		border: `1px solid ${theme.palette.divider}`,
		"&:hover": {
			borderColor: theme.palette.text.secondary,
		},
		"&:focus-within": {
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
		},
	},

	searchIcon: {
		color: theme.palette.text.secondary,
		fontSize: 18,
		marginRight: 8,
	},

	searchInput: {
		flex: 1,
		fontSize: "0.813rem",
		padding: 0,
		"& input": {
			padding: 0,
		},
	},

	searchOptionsBox: {
		flex: "0 0 auto",
		display: "flex",
		flexDirection: "column",
		gap: 8,
		backgroundColor: theme.palette.background.paper,
		padding: "0px 10px 7px",
		borderBottom: `1px solid ${theme.palette.divider}`,
	},

	mediaChipsRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		overflowX: "auto",
		paddingBottom: 2,
		...theme.neoUI.scrollbar.default,
	},

	mediaChip: {
		height: 23,
		fontSize: "0.7rem",
		backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#f0f0f0",
		color: theme.palette.text.primary,
		border: "none",
		"& .MuiChip-label": {
			padding: "0 8px",
		},
		"&.MuiChip-outlined": {
			border: "none",
			backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#f0f0f0",
			color: theme.palette.text.secondary,
		},
		"&.MuiChip-colorPrimary": {
			backgroundColor: "#14A45C",
			color: "#fff",
		},
		"& .MuiChip-icon": {
			color: "inherit",
			fontSize: 16,
			marginLeft: 4,
		},
		"&.MuiChip-clickable:hover": {
			filter: "brightness(0.95)",
		},
	},

	newTicketButton: {
		minWidth: 32,
		width: 32,
		height: 32,
		padding: 0,
		borderRadius: "50%",
		alignSelf: "center",
		[theme.breakpoints.down("xs")]: { minWidth: 44, width: 44, height: 44 },
		"& .MuiIconButton-label": {
			fontSize: 20,
		},
	},

	badge: {
		display: "inline-flex",
		alignItems: "center",
		"& .MuiBadge-badge": {
			fontSize: "0.65rem",
			height: 16,
			minWidth: 16,
			padding: "0 3px",
			backgroundColor: "#f44336 !important", // Cor vermelha do badge do avatar
			color: "white",
			fontWeight: 700,
			boxShadow: "0 2px 4px rgba(244, 67, 54, 0.4)", // Efeito de sombra
			borderRadius: 8,
			top: 1,
		},
		[theme.breakpoints.down("xs")]: {
			"& .MuiBadge-badge": {
				fontSize: "0.6rem",
				height: 14,
				minWidth: 14,
				padding: "0 2px",
				top: 0,
			},
		},
		"@media (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
			"& .MuiBadge-badge": {
				fontSize: "0.6rem",
				height: 14,
				minWidth: 14,
				padding: "0 2px",
				right: -4,
			},
		},
	},

	ticketsListWrapper: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		backgroundColor: theme.palette.background.default,
		borderBottomLeftRadius: 12,
		borderBottomRightRadius: 0,
		[theme.breakpoints.down("sm")]: {
			borderBottomLeftRadius: 0,
		},
		minHeight: 0,
		position: "relative",
	},

	filterSection: {
		flex: "0 0 auto",
		padding: "4px 10px",
		backgroundColor: theme.palette.background.paper,
		borderBottom: `none`,
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		minHeight: 48,
		flexWrap: "wrap",
		"& .MuiFormControl-root": {
			maxWidth: 240,
		},
		"& .MuiOutlinedInput-root": {
			height: 32,
			fontSize: "0.813rem",
		},
		"& .MuiInputLabel-root": {
			fontSize: "0.813rem",
			transform: "translate(16px, 6px) scale(1)",
			"&.MuiInputLabel-shrink": {
				transform: "translate(16px, -7px) scale(0.7)",
			},
		},
		"& .MuiSelect-select": {
			paddingTop: 6,
			paddingBottom: 6,
		},
		"& .MuiAutocomplete-tagSizeSmall": {
			margin: 0,
			padding: 0,
		},
	},

	filterControls: {
		flex: 1,
		display: "flex",
		gap: 10,
		alignItems: "center",
		alignSelf: "center",
		'& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input': {
			padding: "0px 0px !important",
		},
	},

	filterChip: {
		height: 28,
		fontSize: "0.813rem",
		"& .MuiChip-label": {
			padding: "0 8px",
		},
		"& .MuiChip-icon": {
			fontSize: 16,
			marginLeft: 4,
		},
		"& .MuiChip-deleteIcon": {
			fontSize: 18,
		},
	},

	compactSwitch: {
		height: 44,
		display: "flex",
		alignItems: "center",
		alignSelf: "center",
		margin: "0 8px 0 auto",
		"& .MuiFormControlLabel-label": {
			fontSize: "0.813rem",
			marginLeft: 4,
			whiteSpace: "nowrap",
		},
		[theme.breakpoints.down("xs")]: {
			margin: 0,
			flexShrink: 0,
			"& .MuiFormControlLabel-label": {
				fontSize: "0.75rem",
				marginLeft: 1,
			},
		},
		"@media (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
			margin: "0 2px 0 2px",
			"& .MuiSwitch-root": {
				width: 38,
			},
			"& .MuiFormControlLabel-label": {
				fontSize: "0.75rem",
				marginLeft: 1,
			},
		},
	},

	compactQueue: {
		"& .MuiFormControl-root": {
			width: "100%",
		},
		"& .MuiOutlinedInput-root": {
			height: 32,
			fontSize: "0.813rem",
		},
		"& .MuiInputLabel-root": {
			fontSize: "0.813rem",
		},
		"& .MuiSelect-icon": {
			fontSize: 20,
		},
	},

	filtersRow: {
		display: 'flex',
		width: '100%',
		gap: theme.spacing(1.5),
		alignItems: 'center',
		justifyContent: 'space-between',
		'& > div': {
			flex: '1 1 0',
			minWidth: 0,
			maxWidth: 'none',
		},
		'& .MuiFormControl-root': {
			width: '100%',
		},
		'& .MuiOutlinedInput-root': {
			height: 32,
			fontSize: '0.813rem',
		},
		'& .MuiInputLabel-root': {
			fontSize: '0.813rem',
		},
		'& .MuiSelect-icon': {
			fontSize: 20,
		},
	},

	tabsInternalContainer: {
		display: 'flex',
		alignItems: 'center',
		padding: '0 4px',
		minHeight: 44,
		overflow: 'visible',
		[theme.breakpoints.down("xs")]: {
			"& .MuiTabs-root": {
				height: 48,
				minHeight: 48,
			},
		},
		'& .MuiTabs-root': {
			flex: 1,
			minWidth: 0,
			height: 44,
			overflow: 'visible',
			alignSelf: 'center',
			boxShadow: "none !important",
			backgroundColor: 'transparent',
		},
		"@media (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
			padding: "0 2px",
			"& .MuiTabs-root": {
				flex: "1 1 auto",
			},
		},
	},

	tabIcon: {
		fontSize: 18,
		"@media (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
			fontSize: 15,
		},
	},

	chipIcon: {
		fontSize: 16,
	},

	filterIcon: {
		fontSize: 16,
	},

	buttonIcon: {
		fontSize: 18,
		"@media (pointer: coarse) and (min-width: 600px) and (min-height: 600px)": {
			fontSize: 16,
		},
	},

	tabPanelContainer: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		height: "100%",
		minHeight: 0,
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
		borderTopLeftRadius: 0,
	},

	ticketsPanelsContent: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		minHeight: 0,
	},
}));

const TicketsManagerTabs = () => {
	const classes = useStyles();
	const history = useHistory();

	const { user } = useContext(AuthContext);
	const { profile, allTicket, allowNoQueue } = user;
	const isAdmin = (profile || "").toString().toUpperCase() === "ADMIN";
	const canViewAllTickets = isAdmin || allTicket === "enabled";

	// Estado para priorizar não lidos
	const getInitialPrioritizeUnread = () => {
		const companyId = localStorage.getItem("companyId");
		if (companyId) {
			const saved = localStorage.getItem(`tickets:${companyId}:prioritizeUnread`);
			return saved === "true";
		}
		return false;
	};

	const [prioritizeUnread, setPrioritizeUnread] = useState(getInitialPrioritizeUnread);
	const [openCount, setOpenCount] = useState(0);
	const [pendingCount, setPendingCount] = useState(0);
	const normalizeIds = useCallback(
		(values) =>
			(Array.isArray(values) ? values : [])
				.map((value) => Number(value))
				.filter((value) => Number.isFinite(value)),
		[]
	);

	const defaultQueueIds = useMemo(() => {
		const queueIds = (user?.queues || []).map((q) => q.id);
		if (allowNoQueue) {
			return Array.from(new Set([0, ...queueIds]));
		}
		return queueIds;
	}, [user, allowNoQueue]);

	// Persistir preferência de priorizar não lidos
	useEffect(() => {
		const companyId = localStorage.getItem("companyId");
		if (companyId) {
			localStorage.setItem(`tickets:${companyId}:prioritizeUnread`, prioritizeUnread ? "true" : "false");
		}
	}, [prioritizeUnread]);

	// Inicializar estados com localStorage
	const getInitialQueueIds = () => {
		const companyId = localStorage.getItem("companyId");
		if (companyId) {
			const saved = localStorage.getItem(`ticketsFilters:${companyId}:queues`);
			if (saved) {
				try {
					const parsed = JSON.parse(saved);
					if (Array.isArray(parsed)) {
						return parsed.filter((id) => id !== 0 || allowNoQueue);
					}
				} catch (err) {
					console.error("Erro ao restaurar filtro de filas", err);
				}
			}
		}
		return defaultQueueIds;
	};

	const getInitialTags = () => {
		const companyId = localStorage.getItem("companyId");
		if (companyId) {
			const saved = localStorage.getItem(`ticketsFilters:${companyId}:tags`);
			if (saved) {
				try {
					const parsed = JSON.parse(saved);
					if (Array.isArray(parsed)) {
						return normalizeIds(parsed);
					}
				} catch (err) {
					console.error("Erro ao restaurar filtro de tags", err);
				}
			}
		}
		return [];
	};

	const getInitialUsers = () => {
		const companyId = localStorage.getItem("companyId");
		if (companyId) {
			const saved = localStorage.getItem(`ticketsFilters:${companyId}:users`);
			if (saved) {
				try {
					const parsed = JSON.parse(saved);
					if (Array.isArray(parsed)) {
						return parsed;
					}
				} catch (err) {
					console.error("Erro ao restaurar filtro de usuários", err);
				}
			}
		}
		return [];
	};

	// State modification for persistence
	const [tab, setTab] = useState(() => {
		const saved = localStorage.getItem("tickets:tab");
		return saved || "open";
	});

	const [tabOpen, setTabOpen] = useState(() => {
		const saved = localStorage.getItem("tickets:tabOpen");
		return saved || "pending";
	});

	const [searchParam, setSearchParam] = useState(() => {
		const saved = localStorage.getItem("tickets:searchParam");
		return saved || "";
	});

	const [searchMediaFilter, setSearchMediaFilter] = useState(() => {
		const saved = localStorage.getItem("tickets:searchMediaFilter");
		return saved || "";
	});

	const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
	const searchInputRef = useRef();

	// Persist state changes
	useEffect(() => {
		localStorage.setItem("tickets:tab", tab);
	}, [tab]);

	useEffect(() => {
		localStorage.setItem("tickets:tabOpen", tabOpen);
	}, [tabOpen]);

	useEffect(() => {
		localStorage.setItem("tickets:searchParam", searchParam);
	}, [searchParam]);

	useEffect(() => {
		localStorage.setItem("tickets:searchMediaFilter", searchMediaFilter);
	}, [searchMediaFilter]);

	const [selectedQueueIds, setSelectedQueueIds] = useState(getInitialQueueIds);
	const [selectedTags, setSelectedTags] = useState(getInitialTags);
	const [selectedUsers, setSelectedUsers] = useState(getInitialUsers);

	const handleQueueSelection = useCallback(
		(values) => {
			if (!allowNoQueue && values.includes(0)) {
				return;
			}
			setSelectedQueueIds(values);
		},
		[allowNoQueue]
	);

	useEffect(() => {
		if (!allowNoQueue) {
			setSelectedQueueIds((prev) => prev.filter((id) => id !== 0));
		}
	}, [allowNoQueue]);

	useEffect(() => {
		// Só atualiza selectedQueueIds se não houver valor salvo no localStorage
		const companyId = localStorage.getItem("companyId");
		if (companyId) {
			const saved = localStorage.getItem(`ticketsFilters:${companyId}:queues`);
			if (saved === null) {
				// Se não há valor salvo, usa defaultQueueIds
				setSelectedQueueIds(defaultQueueIds);
				// E salva no localStorage
				localStorage.setItem(`ticketsFilters:${companyId}:queues`, JSON.stringify(defaultQueueIds));
			}
		}
	}, [defaultQueueIds]);

	useEffect(() => {
		if (tab === "search" && searchInputRef.current) {
			searchInputRef.current.focus({ preventScroll: true });
		}
	}, [tab]);

	useEffect(() => {
		const handleExternalTabChange = (event) => {
			const { tab: desiredTab } = event.detail || {};
			if (!desiredTab) {
				return;
			}

			setTab(desiredTab);

			if (desiredTab === "open") {
				setTabOpen("open");
			}
		};

		window.addEventListener("tickets:change-tab", handleExternalTabChange);

		return () => {
			window.removeEventListener("tickets:change-tab", handleExternalTabChange);
		};
	}, []);

	let searchTimeout;

	const handleSearch = (e) => {
		const searchedTerm = e.target.value.toLowerCase();

		clearTimeout(searchTimeout);

		if (searchedTerm === "") {
			setSearchParam(searchedTerm);
			setSearchMediaFilter("");
			return;
		}

		searchTimeout = setTimeout(() => {
			setSearchParam(searchedTerm);
		}, 500);
	};

	const handleMediaChipClick = (filter) => {
		setSearchMediaFilter((prev) => (prev === filter ? "" : filter));
	};

	useEffect(() => {
		if (document.querySelector("main[data-mobile-document-scroll]")) window.scrollTo(0, 0);
	}, [tab, tabOpen]);

	const handleChangeTab = (e, newValue) => {
		setTab(newValue);
	};

	const handleChangeTabOpen = (e, newValue) => {
		setTabOpen(newValue);
	};



	const handleCloseOrOpenTicket = (ticket) => {
		setNewTicketModalOpen(false);
		if (!ticket) {
			return;
		}

		setTab("open");
		setTabOpen("open");

		if (typeof window !== "undefined") {
			window.dispatchEvent(
				new CustomEvent("ticket:patched", {
					detail: { ticket }
				})
			);
		}

		const path = ticket.id ? `/tickets/${ticket.id}` : ticket.uuid ? `/tickets/${ticket.uuid}` : null;
		if (path) {
			history.push(path, { ticket, createdFromModal: true });
		}
	};

	const handleSelectedTags = useCallback(
		(tagIds) => {
			setSelectedTags(normalizeIds(tagIds));
		},
		[normalizeIds]
	);

	const handleSelectedUsers = (selecteds) => {
		const users = selecteds.map((t) => t.id);
		setSelectedUsers(users);
	};

	useEffect(() => {
		const companyId = localStorage.getItem("companyId");
		if (!companyId) {
			return;
		}

		localStorage.setItem(
			`ticketsFilters:${companyId}:tags`,
			JSON.stringify(normalizeIds(selectedTags))
		);
	}, [selectedTags, normalizeIds]);

	return (
		<div className={classes.root}>
			<NewTicketModal
				modalOpen={newTicketModalOpen}
				onClose={(ticket) => {
					handleCloseOrOpenTicket(ticket);
				}}
			/>

			{/* Paper principal sem espaçamentos extras */}
			<Paper elevation={0} className={classes.ticketsWrapper}>
				<div className={classes.ticketHeader} data-mobile-ticket-header>
					<Paper elevation={0} square className={classes.tabsHeader} data-mobile-ticket-tabs>
						<Tabs
							value={tab}
							onChange={handleChangeTab}
							variant="fullWidth"
							indicatorColor="primary"
							textColor="primary"
						>
							<Tab
								value={"open"}
								icon={<FolderOpenIcon className={classes.tabIcon} />}
								label={i18n.t("tickets.tabs.open.title")}
								classes={{ root: classes.tab }}
							/>
							<Tab
								value={"closed"}
								icon={<CheckCircleOutlineIcon className={classes.tabIcon} />}
								label={i18n.t("tickets.tabs.closed.title")}
								classes={{ root: classes.tab }}
							/>
							<Tab
								value={"search"}
								icon={<FilterListIcon className={classes.tabIcon} />}
								label={i18n.t("tickets.tabs.search.title")}
								classes={{ root: classes.tab }}
							/>
						</Tabs>
					</Paper>
					{tab !== "search" && (
						<Paper square elevation={0} className={classes.ticketOptionsBox} data-mobile-ticket-options>
							<div className={classes.filtersRow}>
								<div className={classes.compactQueue}>
									<TicketsQueueSelect
										selectedQueueIds={selectedQueueIds}
										userQueues={user?.queues}
										onChange={handleQueueSelection}
										allowNoQueueOption={allowNoQueue}
									/>
								</div>
								<TagsFilter value={selectedTags} onChange={handleSelectedTags} />
								{canViewAllTickets && (
									<UsersFilter onFiltered={handleSelectedUsers} />
								)}
							</div>
						</Paper>
					)}
					{tab === "open" && (
						<Paper elevation={0} square className={classes.tabsInternal} data-mobile-ticket-status>
							<div className={classes.tabsInternalContainer}>
								<Tooltip title={i18n.t("ticketsManager.buttons.newTicket")}>
									<IconButton
										color="primary"
										onClick={() => setNewTicketModalOpen(true)}
										className={classes.newTicketButton}
										size="small"
									>
										<AddCircleOutlineIcon className={classes.buttonIcon} />
									</IconButton>
								</Tooltip>

								<FormControlLabel
									className={classes.compactSwitch}
									label={i18n.t("tickets.buttons.unreadFirst")}
									labelPlacement="end"
									control={
										<Switch
											size="small"
											checked={prioritizeUnread}
											onChange={() =>
												setPrioritizeUnread((prevState) => !prevState)
											}
											name="prioritizeUnread"
											color="primary"
										/>
									}
								/>

								<Tabs
									value={tabOpen}
									onChange={handleChangeTabOpen}
									variant="fullWidth"
									indicatorColor="primary"
									textColor="primary"
								>
									<Tab
										icon={<HourglassEmptyIcon className={classes.tabIcon} />}
										label={
											<Badge
												className={classes.badge}
												badgeContent={pendingCount}
												color="secondary"
												max={9999}
											>
												{i18n.t("ticketsList.pendingHeader")}
											</Badge>
										}
										value={"pending"}
										classes={{ root: classes.internalTab }}
									/>
									<Tab
										icon={<AssignmentTurnedInIcon className={classes.tabIcon} />}
										label={
											<Badge
												className={classes.badge}
												badgeContent={openCount}
												color="primary"
												max={9999}
											>
												{i18n.t("ticketsList.assignedHeader")}
											</Badge>
										}
										value={"open"}
										classes={{ root: classes.internalTab }}
									/>
								</Tabs>
							</div>
						</Paper>
					)}
					{tab === "search" && (
						<>
							<div className={classes.filterSection}>
								<div className={classes.filtersRow}>
									<div className={classes.compactQueue}>
										<TicketsQueueSelect
											selectedQueueIds={selectedQueueIds}
											userQueues={user?.queues}
											onChange={handleQueueSelection}
											allowNoQueueOption={allowNoQueue}
										/>
									</div>
									<TagsFilter value={selectedTags} onChange={handleSelectedTags} />
									{canViewAllTickets && (
										<UsersFilter onFiltered={handleSelectedUsers} />
									)}
								</div>
							</div>
							<Paper square elevation={0} className={classes.searchOptionsBox}>
								<div className={classes.serachInputWrapper}>
									<SearchIcon className={classes.searchIcon} />
									<InputBase
										className={classes.searchInput}
										inputRef={searchInputRef}
										placeholder={i18n.t("tickets.search.placeholder")}
										type="search"
										onChange={handleSearch}
									/>
								</div>

								<div className={classes.mediaChipsRow}>
									<Chip
										className={classes.mediaChip}
										icon={<PhotoCameraIcon />}
										label={i18n.t("tickets.search.mediaChips.photos")}
										clickable
										color={searchMediaFilter === "photos" ? "primary" : "default"}
										variant={searchMediaFilter === "photos" ? "default" : "outlined"}
										onClick={() => handleMediaChipClick("photos")}
									/>
									<Chip
										className={classes.mediaChip}
										icon={<DescriptionIcon />}
										label={i18n.t("tickets.search.mediaChips.documents")}
										clickable
										color={searchMediaFilter === "documents" ? "primary" : "default"}
										variant={searchMediaFilter === "documents" ? "default" : "outlined"}
										onClick={() => handleMediaChipClick("documents")}
									/>
									<Chip
										className={classes.mediaChip}
										icon={<LinkIcon />}
										label={i18n.t("tickets.search.mediaChips.links")}
										clickable
										color={searchMediaFilter === "links" ? "primary" : "default"}
										variant={searchMediaFilter === "links" ? "default" : "outlined"}
										onClick={() => handleMediaChipClick("links")}
									/>
									<Chip
										className={classes.mediaChip}
										icon={<VideocamIcon />}
										label={i18n.t("tickets.search.mediaChips.videos")}
										clickable
										color={searchMediaFilter === "videos" ? "primary" : "default"}
										variant={searchMediaFilter === "videos" ? "default" : "outlined"}
										onClick={() => handleMediaChipClick("videos")}
									/>
								</div>
							</Paper>
						</>
					)}
				</div>

				<div className={classes.ticketsListWrapper}>
					<TabPanel value={tab} name="open" className={classes.tabPanelContainer}>
							<Box className={classes.ticketsPanelsContent}>
								<TicketsList
								status="pending"
								showAll={canViewAllTickets}
								selectedQueueIds={selectedQueueIds}
								tags={selectedTags}
								users={selectedUsers}
								updateCount={(val) => setPendingCount(val)}
								prioritizeUnread={prioritizeUnread}
								style={{ display: tabOpen === "pending" ? "flex" : "none" }}
							/>
							<TicketsList
								status="open"
								showAll={canViewAllTickets}
								selectedQueueIds={selectedQueueIds}
								tags={selectedTags}
								users={selectedUsers}
								updateCount={(val) => setOpenCount(val)}
								prioritizeUnread={prioritizeUnread}
								style={{ display: tabOpen === "open" ? "flex" : "none" }}
							/>
						</Box>
					</TabPanel>

					<TabPanel value={tab} name="closed" className={classes.tabPanelContainer}>
						<TicketsList
							status="closed"
							showAll={true}
							selectedQueueIds={selectedQueueIds}
							tags={selectedTags}
							users={selectedUsers}
							prioritizeUnread={prioritizeUnread}
						/>
					</TabPanel>

					<TabPanel value={tab} name="search" className={classes.tabPanelContainer}>
						<TicketsSearchResults
							searchParam={searchParam}
							mediaFilter={searchMediaFilter || null}
							showAll={true}
							tags={selectedTags}
							users={selectedUsers}
							selectedQueueIds={selectedQueueIds}
						/>
					</TabPanel>
				</div>
			</Paper>
		</div>
	);
};

export default TicketsManagerTabs;
